import type { localeBody } from '@porotutu/api/features/shared/profile/model'
import type { profileResource } from '@porotutu/api/features/shared/profile/resource'
import { unwrap } from '@porotutu/react/api'
import type { ApiError } from '@porotutu/react/errors'
import { mutationOptions, type QueryClient, queryOptions } from '@tanstack/react-query'
import { crm } from '../api/client'
import { PROFILE } from '../api/keys'

export type Profile = ReturnType<typeof profileResource>

export async function readProfile(): Promise<Profile | null> {
  const { data, error } = await crm.profile.get()

  if (error === null) return data

  if (error.status === 401) return null

  throw error
}

export const profileQuery = queryOptions({
  queryKey: PROFILE,
  queryFn: readProfile,
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: Number.POSITIVE_INFINITY,
  retry: false,
})

export type LocaleBody = typeof localeBody.static

export const changeLocaleMutation = (queryClient: QueryClient) =>
  mutationOptions<Profile, ApiError, LocaleBody, { previous: Profile | null | undefined }>({
    mutationKey: [...PROFILE, 'locale'],
    mutationFn: async (body) => unwrap(await crm.profile.locale.patch(body)),
    onMutate: ({ locale }) => {
      const previous = queryClient.getQueryData(profileQuery.queryKey)

      if (previous) {
        const hopeful: Profile = { ...previous, locale }

        queryClient.setQueryData(profileQuery.queryKey, hopeful)
      }

      return { previous }
    },
    onError: (_error, _body, context) => {
      if (context) queryClient.setQueryData(profileQuery.queryKey, context.previous)
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(profileQuery.queryKey, profile)
    },
  })
